import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import Icon from "@mdi/react";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import _ from "lodash";

import { TrackingProperties, Sportsbook, WEB_SPORTS_MAP } from "../../../constants";
import BookInputColumn from "./BookInputColumn";
import InputColumn, { InputColumnItem } from "./InputColumn";
import {
  COMMON_EVENT_SETTINGS,
  LINE_TYPE_SETTINGS,
  NBA_TEAM_STATS,
  SPREAD_EVENT_SETTINGS,
  TOTALS_EVENT_SETTINGS,
  TEAM_BETTING_SETTINGS,
} from "./constants";
import AnalyticsService from "../../../services/AnalyticsService";
import styles from "./styles.scss";
import { useReactiveVarListener } from "src/services/UtilService";
import { gamesModifyStatsEvent } from "src/services/ApolloService";

export interface ColumnItem {
  columnId: number;
  selected: boolean;
  order: number;
}

export interface ColumnItemMap {
  [key: number]: ColumnItem;
}

export interface ModifyStatsDropdownProps {
  activeBookId: string;
  books: Sportsbook[];
  sportUrl: string;
  betType: string;
  activeColumnOrder: number[];
  onCancel: () => void;
  onSave: (selectedBookId: string, columnOrder: number[]) => void;
  style?: object;
  className?: string;
  trackingProps: TrackingProperties;
}

const ToggleModifyStats = ({ onToggle }: { onToggle: any }) => {
  useReactiveVarListener(gamesModifyStatsEvent, (e) => {
    if (e) {
      onToggle?.();
    }
  });
  return null;
};

const listToMap = (columnOrder: number[]) => {
  let columnMap: ColumnItemMap = {};
  columnOrder.forEach((columnId: number, order: number) => {
    columnMap[columnId] = {
      columnId,
      selected: true,
      order,
    };
  });
  return columnMap;
};

const findIndexWithFallback = (id: string, items: { id: string }[]) => {
  let index = items?.findIndex((i) => i.id === id) ?? 0;
  if (index === -1) {
    index = 0;
  }
  return index;
};

const ModifyStatsDropdown = React.memo(
  ({
    activeBookId,
    activeColumnOrder,
    books,
    sportUrl,
    betType,
    onCancel,
    onSave,
    style,
    className,
    trackingProps,
  }: ModifyStatsDropdownProps) => {
    const currentSportObj = WEB_SPORTS_MAP[sportUrl];
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const [columnItemMap, setColumnItemMap] = useState<ColumnItemMap>({});
    const [selectedBookIndex, setSelectedBookIndex] = useState<number>(0);

    // If the external book changes, update our internal index to match
    // Note: onSave the internal index should already be set correctly
    // resulting in a noop.
    useEffect(() => {
      setSelectedBookIndex(findIndexWithFallback(activeBookId, books));
    }, [activeBookId, books]);

    // If the external activeColumnOrder changes we need to update our state internally
    // to match. This can happen if the user saves new columns within this dropdown, or
    // when they select a column order setting from their saved list.
    useEffect(() => {
      // convert columnOrder to columnItemMap and save to state
      setColumnItemMap(listToMap(activeColumnOrder));
    }, [activeColumnOrder]);

    /**
     * Update the index on the books
     */
    const onChangeBook = useCallback((index: number) => setSelectedBookIndex(index), []);

    /**
     * Open/Close the dropdown. This will also set the current state to match
     * the current columnOrder value of the userUiSettings prop via the
     * getSelectedSettings function.
     */
    const toggle = useCallback(() => {
      setDropdownOpen(!dropdownOpen);
      AnalyticsService.track(`Add / Remove Stats ${dropdownOpen ? "Cancel" : "Open"}`, {
        screen: "FeedView",
        settings: columnItemMap,
        numColumns: activeColumnOrder.length,
        ...trackingProps,
      });
    }, [activeColumnOrder, selectedBookIndex, columnItemMap, dropdownOpen, trackingProps]);

    /**
     * This function handles the action of clicking the save button
     * It will update current state then call the unsavedModifyStatsDropdownChanges prop function
     */
    const onSaveChanges = useCallback(() => {
      // Add selected columns to an array
      let coluymnItemArr: ColumnItem[] = [];
      for (const key in columnItemMap) {
        const tempSetting = columnItemMap[key];
        if (tempSetting.selected) {
          coluymnItemArr.push(tempSetting);
        }
      }

      // Sort the array by the order to get them in the correct order for display
      coluymnItemArr.sort((a: ColumnItem, b: ColumnItem) => {
        if (a.order < b.order) {
          return -1;
        }
        if (a.order > b.order) {
          return 1;
        }
        return 0;
      });

      // Map them back to an array of column IDs
      const columnOrder: number[] = coluymnItemArr.map((c: ColumnItem) => c.columnId);
      const bookSelection: Sportsbook = books[selectedBookIndex];
      // Return the bookId and columnOrder externally
      onSave(bookSelection.id, columnOrder);
      // Close the dropdown with analytics
      toggle();
    }, [columnItemMap, dropdownOpen, selectedBookIndex]);

    /** Close the dropdown */
    const onUserCancel = useCallback(() => {
      // Reset back to external values
      setSelectedBookIndex(findIndexWithFallback(activeBookId, books));
      setColumnItemMap(listToMap(activeColumnOrder));
      // Close the dropdown
      toggle();
      // Notify externally that we cancelled
      onCancel();
    }, [activeBookId, activeColumnOrder, books, toggle, onCancel]);

    /**
     * This function handles setting state when boxes are checked and unchecked
     * @param columnId {number} The ID corresponding to the column selected
     *  see this files constants file and bet-web component/GamesTable
     */
    const onChangeSetting = useCallback(
      (columnId: number) => {
        const tempSettings = _.cloneDeep(columnItemMap);
        if (tempSettings[columnId]) {
          tempSettings[columnId].selected = !tempSettings[columnId].selected;
          setColumnItemMap(tempSettings);
        } else {
          // Move all the other columns over 2 position
          for (let key in tempSettings) {
            if (tempSettings[key].order >= 2) {
              tempSettings[key].order += 1;
            }
          }
          // Always add new columns to position 2 in the columnOrder list.
          // This is actually the third column from the view of the user.
          tempSettings[columnId] = {
            columnId,
            selected: true,
            order: 2,
          };
          setColumnItemMap(tempSettings);
        }
      },
      [columnItemMap]
    );

    /** Filter out injuries column if the league doesn't have it. */
    const commonEventSettings = useMemo(() => {
      if (currentSportObj.hasNewsInjuriesColumn) {
        return COMMON_EVENT_SETTINGS;
      }
      return COMMON_EVENT_SETTINGS.filter((i: InputColumnItem) => i.key !== "injury");
    }, [currentSportObj.hasNewsInjuriesColumn]);

    const { teamBettingSettingsFiltered, totalsEventSettingsFiltered, spreadEventSettingsFiltered } = useMemo(() => {
      let teamBetting: InputColumnItem[] = TEAM_BETTING_SETTINGS;
      let totalsEvent: InputColumnItem[] = TOTALS_EVENT_SETTINGS;
      let spreadEvent: InputColumnItem[] = SPREAD_EVENT_SETTINGS;

      if (["ncaaf", "nfl", "xfl"].includes(sportUrl)) {
        teamBetting = TEAM_BETTING_SETTINGS.filter((s: InputColumnItem) => {
          return s.key !== "homeRecord" && s.key !== "roadRecord";
        });
        totalsEvent = TOTALS_EVENT_SETTINGS.filter((s: InputColumnItem) => {
          return s.key !== "homeOURecord" && s.key !== "roadOURecord";
        });
        spreadEvent = SPREAD_EVENT_SETTINGS.filter((s: InputColumnItem) => {
          return s.key !== "homeATSRecord" && s.key !== "roadATSRecord";
        });
      }
      return {
        teamBettingSettingsFiltered: teamBetting,
        totalsEventSettingsFiltered: totalsEvent,
        spreadEventSettingsFiltered: spreadEvent,
      };
    }, [sportUrl]);

    return (
      <Dropdown
        isOpen={dropdownOpen}
        toggle={toggle}
        className={`rotoql-large-dropdown ${className}`}
        direction="down"
        placement="bottom"
      >
        <DropdownToggle
          block
          style={style}
          className={`rotoql-large-dropdown__toggle-button ${
            dropdownOpen ? "rotoql-large-dropdown__toggle-button--active" : ""
          }`}
        >
          <ToggleModifyStats onToggle={toggle} />
          <div className="d-none d-sm-block w-100">
            Modify Stats
            {dropdownOpen ? (
              <Icon path={mdiChevronUp} size="26px" color="#00214D" />
            ) : (
              <Icon path={mdiChevronDown} size="26px" color="#00214D" />
            )}
          </div>
        </DropdownToggle>
        <DropdownMenu className="rotoql-large-dropdown__menu" right flip={false} xplacement="bottom">
          <div className="rotoql-large-dropdown__column-container">
            <div className="rotoql-large-dropdown__half-column-container">
              <BookInputColumn
                className="rotoql-large-dropdown__first-three-column"
                onChangeBook={onChangeBook}
                selectedIndex={selectedBookIndex}
                books={books}
              />
              <InputColumn
                className="rotoql-large-dropdown__first-three-column"
                betType={betType}
                onChangeSetting={onChangeSetting}
                items={LINE_TYPE_SETTINGS}
                columnItems={columnItemMap}
                title={"LINES"}
              />
              <InputColumn
                className="rotoql-large-dropdown__first-three-column"
                onChangeSetting={onChangeSetting}
                items={commonEventSettings}
                columnItems={columnItemMap}
                title={"BETQL ANALYSIS"}
              />
            </div>
            <div className="rotoql-large-dropdown__half-column-container">
              <InputColumn
                onChangeSetting={onChangeSetting}
                items={teamBettingSettingsFiltered}
                columnItems={columnItemMap}
                title={"TEAM BETTING ANALYSIS"}
              />
              <InputColumn
                onChangeSetting={onChangeSetting}
                items={totalsEventSettingsFiltered}
                columnItems={columnItemMap}
                title={"SPREAD BETTING ANALYSIS"}
              />
              <InputColumn
                onChangeSetting={onChangeSetting}
                items={spreadEventSettingsFiltered}
                columnItems={columnItemMap}
                title={"TOTALS BETTING ANALYSIS"}
              />
            </div>
            <div className="rotoql-large-dropdown__half-column-container">
              {sportUrl === "nba" ? (
                <InputColumn
                  onChangeSetting={onChangeSetting}
                  items={NBA_TEAM_STATS}
                  columnItems={columnItemMap}
                  title={"NBA TEAM STATS"}
                />
              ) : null}
            </div>
          </div>
          <div className="d-none d-sm-block">
            <div className="rotoql-large-dropdown__divider" />
            <div className="rotoql-large-dropdown__button-container">
              <Button className="rotoql-large-dropdown__cancel-button" onClick={onUserCancel}>
                Cancel
              </Button>
              <Button className="rotoql-button rotoql-large-dropdown__save-button" onClick={onSaveChanges}>
                Add Settings
              </Button>
            </div>
          </div>
          <div className="d-sm-none rotoql-large-dropdown__sticky-mobile">
            <div className="rotoql-large-dropdown__button-container">
              <Button className="rotoql-large-dropdown__cancel-button" onClick={onUserCancel}>
                Cancel
              </Button>
              <Button className="rotoql-button rotoql-large-dropdown__save-button" onClick={onSaveChanges}>
                Add Settings
              </Button>
            </div>
          </div>
        </DropdownMenu>
        <style jsx global>
          {styles}
        </style>
      </Dropdown>
    );
  }
);

export default ModifyStatsDropdown;
